import { useMemo } from 'react';
import { AccountFormProvider, AccountPageProvider } from '@/features/danh-muc/doi-tuong/he-thong-tai-khoan/context';
import DanhMucNganHangFormDialog from '@/features/danh-muc/ngan-hang/danh-muc-ngan-hang/components/FormDialog';
import AccountFormDialog from '@/features/danh-muc/doi-tuong/he-thong-tai-khoan/components/FormDialog';
import { type QuickCreateAdapter, QuickCreateProvider } from '@/hooks/use-quick-create';
import type { AccountModel, NganHang, NganHangInput } from '@/types/schemas';
import { useCRUD } from '@/hooks/queries/useCRUD';
import { QUERY_KEYS } from '@/constants';

interface QuickCreateScopeProps {
  children: React.ReactNode;
}

export const BANK_QUICK_CREATE_KEY = 'ngan_hang';
export const ACCOUNT_QUICK_CREATE_KEY = 'tai_khoan';

export function QuickCreateScope({ children }: QuickCreateScopeProps) {
  const { addItem, updateItem } = useCRUD<NganHang, NganHangInput>({
    endpoint: QUERY_KEYS.NGAN_HANG,
    initialFetch: false
  });

  const adapters = useMemo<Record<string, QuickCreateAdapter>>(
    () => ({
      [BANK_QUICK_CREATE_KEY]: {
        render: ({ initialData, mode, onClose, onSaved, open }) => (
          <DanhMucNganHangFormDialog
            mode={mode}
            open={open}
            onClose={onClose}
            onSubmit={async data => {
              const savedItem =
                mode === 'edit' && initialData?.uuid ? await updateItem(initialData.uuid, data) : await addItem(data);

              onSaved(savedItem);
            }}
            initialData={mode === 'edit' ? initialData : undefined}
            zIndex={1500}
          />
        )
      },
      [ACCOUNT_QUICK_CREATE_KEY]: {
        render: ({ initialData, mode, onClose, onSaved }) => (
          <AccountPageProvider>
            <AccountFormProvider
              account={mode === 'edit' ? (initialData as AccountModel | undefined) || null : null}
              mode={mode}
              isCopyMode={false}
              onClose={onClose}
              onSuccess={async savedAccount => {
                if (savedAccount) {
                  onSaved(savedAccount);
                  return;
                }

                onClose();
              }}
            >
              <AccountFormDialog zIndex={1500} />
            </AccountFormProvider>
          </AccountPageProvider>
        )
      }
    }),
    [addItem, updateItem]
  );

  return <QuickCreateProvider adapters={adapters}>{children}</QuickCreateProvider>;
}
